import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Send } from "lucide-react";

export default function ChatScreen() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/chat/${id}`);
      setMessages(data);
    } catch (e) {
      console.warn("[Chat] load failed:", e?.message);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/chat", { requestId: id, message: text.trim() });
      setText("");
      load();
    } catch (e) {
      console.warn("[Chat] send failed:", e?.message);
    }
  };

  return (
    <div className="flex flex-col h-screen max-w-[480px] mx-auto border-l border-r border-white/10">
      <div className="px-6 pt-5 pb-4 border-b border-white/10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="chat-back-btn"><ArrowLeft /></button>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Chat</div>
          <h1 className="font-display text-lg font-black leading-none">Request #{id.slice(0, 6)}</h1>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-[#9CA3AF] mt-10">No messages yet. Say hello.</div>
        ) : (
          messages.map((m) => {
            const mine = m.senderId === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-md ${
                  mine ? "bg-[#3CFF7E] text-[#0A0A0A]" : "bg-white/10 text-white"
                }`}>
                  {!mine && (
                    <div className="text-[9px] font-bold uppercase tracking-wider mb-0.5 opacity-70">{m.senderName}</div>
                  )}
                  <div className="text-sm">{m.message}</div>
                  <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-[#9CA3AF]"}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2 bg-[#111827]">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message..."
          data-testid="chat-input"
          className="flex-1 h-12 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
        />
        <button type="submit" data-testid="chat-send-btn" className="h-12 w-12 bg-[#3CFF7E] text-[#0A0A0A] rounded-md flex items-center justify-center active:scale-95">
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
